import './DodMarketFilterPreview.scss';
import React, {useMemo} from 'react';
import classnames from 'classnames';
import {DodFilters, SummedSelection} from '@/types/DodRun';
import {ByzzerChangeEventHandler} from '@byzzer/ui-components';
import {DodFilterPreview, DodFilterPreviewProps, DodSelectionPreviewNode} from '@/components/DodConfigEditor/common';
import {uniqBy} from "lodash";
import { DodSavedSelectionTypes } from '@/components/DodConfigEditor/types';
import { MARKET_CHARACTERISTICS_HIERARCHY_ORDER_IDS } from '@/components/DodConfigEditor/common/utils';

const baseClassName = 'dod-market-filter-preview';

export type DodMarketFilterPreviewProps = {
    value: DodFilters;
    onChange?: ByzzerChangeEventHandler<DodFilters>;
} & Partial<DodFilterPreviewProps>;

const savedSelectionDescription = <>
    <p>This will save the full set of selections currently in your Selection panel so that you can easily reuse them in
        future extracts.</p>
    <p>Your saved selections will be available in the "My Saved Markets" folder on the left.</p>
</>;

const characteristicLabelMapping: Record<string, string> = {
    marketType: 'Market Type',
    marketLevel: 'Market Level',
    marketTier: 'National vs Regional',
    parentRetailer: 'Parent Retailer',
    parentCompany: 'Parent Retailer',
    channel: 'Channel',
    childMarketType: 'Child Market Type',
};

export const marketsInitialGroups: DodSelectionPreviewNode[] = [
    {
        label: 'Markets',
        filterType: "marketTypes",
        loadChildren(filters) {
            let totalUsMarkets = filters.markets.values.filter((mrkt) => mrkt.selectionType === 'Total US');
            let fmcgMarkets = filters.markets.values.filter((mrkt) => mrkt.selectionType === 'By Channel FMCG');
            let specialityMarkets = filters.markets.values.filter(
                (mrkt) => mrkt.selectionType === 'By Channel Speciality'
            );
            let geographyMarkets = filters.markets.values.filter((mrkt) => mrkt.selectionType === 'By Geography');
            let summedMarkets = filters.markets.summedSelections;

            return [
                {
                    label: 'Total US',
                    values: totalUsMarkets.map((mrkt) => mrkt.name),
                    summedSelections: [],
                    filterType: {
                        type: 'markets',
                        data: 'Total US'
                    }
                },
                {
                    label: 'By Channel FMCG',
                    values: fmcgMarkets.map((mrkt) => mrkt.name),
                    summedSelections: [],
                    filterType: {
                        type: 'markets',
                        data: 'By Channel FMCG'
                    }
                },
                {
                    label: 'By Channel Speciality',
                    values: specialityMarkets.map((mrkt) => mrkt.name),
                    summedSelections: [],
                    filterType: {
                        type: 'markets',
                        data: 'By Channel Speciality'
                    }
                },
                {
                    label: 'By Geography',
                    values: geographyMarkets.map((mrkt) => mrkt.name),
                    summedSelections: [],
                    filterType: {
                        type: 'markets',
                        data: 'By Geography'
                    }
                },
                {
                    label: 'Summed Markets',
                    values: [],
                    summedSelections: summedMarkets.map((sum) => {
                        return {
                            name: sum.name,
                            values: sum.values.map(value => value.name),
                        } as SummedSelection<string[]>;
                    }),
                    filterType: {
                        type: 'markets',
                        data: 'summed'
                    }
                },
            ] as DodSelectionPreviewNode[];
        },
    },
    {
        label: 'Characteristics',
        filterType: 'marketCharacteristics',
        loadChildren(filters) {
            // Get all entries and sort by hierarchy order
            const entries = Object.entries(filters.marketCharacteristics || {});
            entries.sort(([idA], [idB]) => {
                const indexA = MARKET_CHARACTERISTICS_HIERARCHY_ORDER_IDS.indexOf(idA);
                const indexB = MARKET_CHARACTERISTICS_HIERARCHY_ORDER_IDS.indexOf(idB);
                
                // Both found in hierarchy: sort by hierarchy position
                if (indexA !== -1 && indexB !== -1) {
                    return indexA - indexB;
                }
                // Only A found: A comes first
                if (indexA !== -1) return -1;
                // Only B found: B comes first
                if (indexB !== -1) return 1;
                // Neither found: alphabetical
                return idA.localeCompare(idB);
            });

            return entries.map(([id, selection]) => {
                return {
                    label: characteristicLabelMapping[id] || id,
                    values: selection.values,
                    summedSelections: [],
                    filterType: {
                        type: 'marketCharacteristics',
                        data: { id }
                    }
                } as DodSelectionPreviewNode;
            });
        }
    }
];

export function DodMarketFilterPreview({ className, ...props }: DodMarketFilterPreviewProps) {

    const count = useMemo<number>(() => {
        const markets = props.value.markets;
        if (!markets || !markets.values || !markets.summedSelections) {
            return 0;
        }
        const {values, summedSelections} = markets;
        const allValues = [...values, ...summedSelections.map(ss => ss.values)].flat();
        return uniqBy(allValues, 'key').length;
    }, [props.value])

    return (
        <DodFilterPreview
            {...props}
            className={classnames(baseClassName, className)}
            savedSelectionType={DodSavedSelectionTypes.MARKET}
            emptyStateContent={'No Markets Selected'}
            initialGroups={marketsInitialGroups}
            title={count > 0 ? `${count} Selected` : ''}
            selectionCount={count}
            savedSelectionDescription={savedSelectionDescription}
        />
    );
}

export default DodMarketFilterPreview;
